import { Flex, Stack, Text, useCheckboxState } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useWatchAlias } from 'hooks'
import { useIntl } from 'react-intl'
import { DefaultProps, Restrictions } from 'typings'
import { useFormStateContext } from '../../../../../providers'
import { FormAliasKeys } from '../../../../../providers/useFormAlias.constate'
import { CollapsibleCheckbox } from '../../../../CollapsibleCheckbox'
import { IntegerInput } from '../../../../IntegerInput'
import {
  RestrictionElementProps,
  RestrictionFieldProps,
} from '../../hooks/useRestrictions.constate'
import { ApplyTo } from '../../typings'
import { ApplyToField } from '../applyToField'
import { RestrictionsRegister } from '../Component.register'
import { messages } from '../messages'
import styles from '../styles.css'

export function showApplyTo(benefitType: string | undefined): boolean {
  return benefitType === 'maxPrice'
}

export const MaxItemsPerCart = (props: RestrictionFieldProps) =>
  RestrictionsRegister({ Element: MaxItemsPerCartEditor, props })

const EMPTY_CONFIG = Object.freeze({
  value: 1,
})

export function MaxItemsPerCartEditor(
  props: RestrictionElementProps & DefaultProps
) {
  const { name, form } = props

  const { $t } = useIntl()
  const { form: mainForm } = useFormStateContext()

  const benefitType = useWatchAlias<string>({
    form: mainForm,
    key: FormAliasKeys.benefitType,
  })

  const maxItems: Restrictions['maxItemsPerCart'] = useWatch({
    form,
    name,
  })

  const handleChange = ({ target: { checked } }) => {
    if (!checked) {
      form.resetField(name)
      form.setValue(name, undefined)
    } else {
      const emptyConfig = showApplyTo(benefitType)
        ? { ...EMPTY_CONFIG, applyTo: ApplyTo.cheapest }
        : { ...EMPTY_CONFIG }
      form.setValue(name, emptyConfig)
    }
  }

  const state = useCheckboxState({ initialValue: maxItems !== undefined })

  return (
    <CollapsibleCheckbox
      state={state}
      label={$t(messages.maxItemsPerCart.label)}
      onChange={handleChange}
    >
      <Stack space="$space-5" fluid>
        <Flex align="baseline">
          <div className={styles.inputContainer}>
            <IntegerInput
              name={`${name}.value`}
              state={form}
              aria-label={$t(messages.maxItemsPerCart.ariaLabel)}
              min={1}
            />
          </div>

          <Text tone="secondary">
            {$t(messages.items, { value: maxItems?.value })}
          </Text>
        </Flex>
        {showApplyTo(benefitType) && (
          <ApplyToField name={`${name}.applyTo`} form={form} />
        )}
      </Stack>
    </CollapsibleCheckbox>
  )
}
